/**
 * Implementação remota do repositório de Dispositivos
 */

import type { DispositivoRepository } from '../core/dispositivo_repository';
import type { 
  Dispositivo, 
  DispositivoCompacto, 
  AdicionarDispositivoRequest,
  AceitarTermosRequest
} from '../core/models';
import { RemoteDataSource } from '@/datasources/remote_datasource';
import { deviceInfoService } from '@/services/deviceInfoService';

export class RemoteDispositivoRepository implements DispositivoRepository {
  private dataSource: RemoteDataSource;

  constructor(dataSource: RemoteDataSource) {
    this.dataSource = dataSource;
  }

  async adicionarDispositivo(request: AdicionarDispositivoRequest): Promise<Dispositivo> {
    const deviceInfo = await deviceInfoService.getDeviceInfo();
    const body = {
      ...request,
      dispositivoId: deviceInfo.dispositivoId,
      dispositivoHash: deviceInfo.dispositivoHash,
    };

    const response = await this.dataSource.post<Dispositivo>({
      endpoint: 'v1/dispositivos',
      body: body as unknown as Record<string, unknown>,
      headers: { 'accept': 'application/vnd.data.full.v1+json' },
      serialize: (data) => data as Dispositivo,
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }

  async aceitarTermos(request: AceitarTermosRequest): Promise<void> {
    const response = await this.dataSource.post({
      endpoint: 'v1/dispositivos/aceitar-termos',
      body: request as unknown as Record<string, unknown>,
      headers: { 'accept': 'application/vnd.data.minimal.v1+json' },
    });

    if ('failure' in response) {
      throw new Error(response.failure.messages.join(', '));
    }
  }


  async obterDispositivos(compacto = false): Promise<Dispositivo[] | DispositivoCompacto[]> {
    const accept = compacto 
      ? 'application/vnd.data.compact.v1+json'
      : 'application/vnd.data.full.v1+json';
    
    const response = await this.dataSource.get<Dispositivo[] | DispositivoCompacto[]>({
      endpoint: 'v1/dispositivos',
      headers: { accept },
      serialize: (data) => data as Dispositivo[] | DispositivoCompacto[],
      forceRefresh: true,
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }

  async obterDispositivoPorId(
    dispositivoId: string, 
    compacto = false
  ): Promise<Dispositivo | DispositivoCompacto> {
    const accept = compacto 
      ? 'application/vnd.data.compact.v1+json'
      : 'application/vnd.data.full.v1+json';
    
    const response = await this.dataSource.get<Dispositivo | DispositivoCompacto>({
      endpoint: `v1/dispositivos/${dispositivoId}`,
      headers: { accept },
      serialize: (data) => data as Dispositivo | DispositivoCompacto,
      forceRefresh: true,
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }

  async removerDispositivo(dispositivoId: string): Promise<void> {
    const response = await this.dataSource.delete({
      endpoint: `v1/dispositivos/${dispositivoId}`,
      headers: { 'accept': 'application/force+json' },
    });

    if ('failure' in response) {
      throw new Error(response.failure.messages.join(', '));
    }
  }
}
